export type MessageHandler<T = any> = (data: T) => void;

export type MessageHandlers = {
  [messageType: string]: MessageHandler;
};

export enum ConnectionState {
  DISCONNECTED = "disconnected",
  CONNECTING = "connecting",
  CONNECTED = "connected",
  RECONNECTING = "reconnecting",
}

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private wsUrl: string;
  private tokenFetcher: () => Promise<string>;
  private handlers: MessageHandlers;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private state: ConnectionState = ConnectionState.DISCONNECTED;
  private manualDisconnect = false;
  private onStateChange?: (state: ConnectionState) => void;

  constructor(
    wsUrl: string,
    tokenFetcher: () => Promise<string>,
    handlers: MessageHandlers,
    onStateChange?: (state: ConnectionState) => void
  ) {
    this.wsUrl = wsUrl;
    this.tokenFetcher = tokenFetcher;
    this.handlers = handlers;
    this.onStateChange = onStateChange;
  }

  private async fetchToken(): Promise<string> {
    return this.tokenFetcher();
  }

  private getWebSocketUrl(token: string): string {
    return `${this.wsUrl}?token=${encodeURIComponent(token)}`;
  }

  /**
   * Update connection state and notify listeners
   */
  private setState(state: ConnectionState): void {
    if (this.state !== state) {
      this.state = state;
      this.onStateChange?.(state);
    }
  }

  /**
   * Get current connection state
   */
  public getState(): ConnectionState {
    return this.state;
  }

  /**
   * Connect to the WebSocket server
   */
  public async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN || this.state === ConnectionState.CONNECTING) {
      console.warn("WebSocket already connected or connecting");
      return;
    }

    this.manualDisconnect = false;
    this.setState(ConnectionState.CONNECTING);

    try {
      // Fetch authentication token
      const token = await this.fetchToken();
      const url = this.getWebSocketUrl(token);
      console.log(`Connecting to WebSocket: ${url}`);

      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log("WebSocket connected");
        this.setState(ConnectionState.CONNECTED);
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          const { type, ...data } = message;

          if (type && this.handlers[type]) {
            this.handlers[type](data);
          } else {
            console.warn(`No handler for message type: ${type}`, message);
          }
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
        }
      };

      this.ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        this.handlers.error?.({ message: "WebSocket error occurred" });
      };

      this.ws.onclose = (event) => {
        console.log(`WebSocket closed: ${event.code} ${event.reason}`);
        this.setState(ConnectionState.DISCONNECTED);
        this.ws = null;

        // Auto-reconnect if not manually disconnected
        if (!this.manualDisconnect) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error("Failed to create WebSocket:", error);
      this.setState(ConnectionState.DISCONNECTED);
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      this.handlers.error?.({ message: "Failed to reconnect after maximum attempts" });
      return;
    }

    this.setState(ConnectionState.RECONNECTING);
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(async () => {
      await this.connect();
    }, delay);
  }

  /**
   * Send a message to the WebSocket server
   */
  public send(type: string, payload: any = {}): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not connected, cannot send message");
      return;
    }

    const message = { type, ...payload };
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Disconnect from the WebSocket server
   */
  public disconnect(): void {
    this.manualDisconnect = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, "Manual disconnect");
      this.ws = null;
    }

    this.setState(ConnectionState.DISCONNECTED);
  }

  /**
   * Check if WebSocket is connected
   */
  public isConnected(): boolean {
    return this.state === ConnectionState.CONNECTED && this.ws?.readyState === WebSocket.OPEN;
  }
}
